<template>
  <rmr-modal
    :value="value"
    variant="form"
    eyebrow="Demande d'ajout de sources"
    :title="title"
    :subtitle="subtitle"
    :icon="icons.mdiRssBox"
    :max-width="1100"
    @input="$emit('input', $event)"
  >
    <template #body>
      <v-card-text class="pa-6">
        <div v-if="request" class="srq-detail__summary">
          <rmr-status-chip
            :label="requestStatus(request.status).label"
            :variant="requestStatus(request.status).variant"
            fixed
          />
          <span class="srq-detail__counts">
            {{ request.total }} URL ·
            <strong class="srq-detail__ok">{{ request.accepted_count }}</strong>
            acceptée{{ request.accepted_count > 1 ? "s" : "" }} ·
            <strong class="srq-detail__ko">{{ request.rejected_count }}</strong>
            refusée{{ request.rejected_count > 1 ? "s" : "" }}
            <template v-if="request.error_count">
              ·
              <strong class="srq-detail__err">{{ request.error_count }}</strong>
              en erreur
            </template>
          </span>
          <span v-if="isOpen" class="srq-detail__pending">
            Vérification en cours : la demande est traitée dès son envoi (sinon
            par le robot, toutes les 10 minutes), cette fenêtre se met à jour
            toute seule.
          </span>
        </div>

        <rmr-notice v-if="request && request.error" variant="error">
          {{ request.error }}
        </rmr-notice>

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          item-key="id"
          dense
          class="rmr-data-table"
          :items-per-page="50"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        >
          <template #[`item.raw_url`]="{ item }">
            <a
              :href="item.raw_url"
              target="_blank"
              rel="noopener"
              class="srq-detail__url"
              :title="item.raw_url"
            >
              {{ item.raw_url }}
            </a>
          </template>

          <template #[`item.status`]="{ item }">
            <rmr-status-chip
              :label="itemStatus(item.status).label"
              :variant="itemStatus(item.status).variant"
              size="sm"
              fixed
            />
          </template>

          <template #[`item.reason_code`]="{ item }">
            <div class="srq-detail__reason">
              {{ reasonLabel(item.reason_code) }}
            </div>
            <div
              v-if="item.reason_detail"
              class="srq-detail__reason-detail"
              :title="item.reason_detail"
            >
              <span class="srq-detail__marquee">{{ item.reason_detail }}</span>
            </div>
          </template>

          <template #[`item.source`]="{ item }">
            <template v-if="item.news_radar_source">
              <div class="srq-detail__reason">
                {{ item.news_radar_source.name }}
                <span v-if="item.media_type" class="srq-detail__media">
                  · {{ mediaTypeLabel(item.media_type) }}
                </span>
              </div>
              <div class="srq-detail__reason-detail" :title="item.entry">
                <span class="srq-detail__marquee">{{ item.entry }}</span>
              </div>
            </template>
          </template>
        </v-data-table>
      </v-card-text>
    </template>

    <template #footer>
      <rmr-action-button
        variant="ghost"
        label="Fermer"
        @click="$emit('input', false)"
      />
    </template>
  </rmr-modal>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import { mdiRssBox } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

import useAxios from "@/hooks/useAxios";
import { formatDateHumanReadableDayHoursMinutes } from "@/utils/dates";

import {
  isRequestOpen,
  itemStatus,
  mediaTypeLabel,
  reasonLabel,
  requestStatus,
} from "./labels";

const POLL_DELAY_MS = 10000;

export default {
  name: "SourceRequestDetail",
  components: {
    RmrActionButton,
    RmrModal,
    RmrNotice,
    RmrStatusChip,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    requestId: {
      type: Number,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();

    const request = ref(null);
    const loading = ref(false);
    let pollTimer = null;

    const headers = [
      { text: "#", value: "position", width: "56px" },
      { text: "URL demandée", value: "raw_url" },
      { text: "Statut", value: "status", width: "120px" },
      { text: "Résultat", value: "reason_code" },
      { text: "Source", value: "source", sortable: false },
    ];

    // =>  COMPUTED
    const items = computed(() =>
      (request.value?.items || []).map((item) => ({
        ...item,
        position: item.position + 1,
      }))
    );
    const isOpen = computed(() => isRequestOpen(request.value?.status));
    const title = computed(() =>
      props.requestId ? `Demande #${props.requestId}` : "Demande"
    );
    const subtitle = computed(() => {
      if (!request.value) return "";
      const parts = [
        request.value.site?.name,
        mediaTypeLabel(request.value.media_type),
        request.value.requested_by,
        formatDateHumanReadableDayHoursMinutes(request.value.created_at),
      ].filter(Boolean);
      return parts.join(" · ");
    });

    // =>  METHODS
    const stopPolling = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const fetchDetail = async (withSpinner = true) => {
      stopPolling();
      if (!props.value || !props.requestId) return;
      const requestId = props.requestId;
      if (withSpinner) loading.value = true;
      try {
        const { data } = await axiosGet(
          `/news-radar-source-requests/${requestId}`
        );
        // La fenêtre a pu changer de demande pendant l'appel.
        if (requestId !== props.requestId || !props.value) return;
        const wasOpen = isRequestOpen(request.value?.status);
        request.value = data;
        if (isRequestOpen(data.status)) {
          pollTimer = setTimeout(() => fetchDetail(false), POLL_DELAY_MS);
        } else if (wasOpen) {
          emit("settled", data);
        }
      } catch (error) {
        console.error("Error fetching source request:", error);
      } finally {
        loading.value = false;
      }
    };

    // =>  WATCH
    watch(
      () => [props.value, props.requestId],
      ([open]) => {
        if (open) {
          request.value = null;
          fetchDetail();
        } else {
          stopPolling();
        }
      },
      { immediate: true }
    );

    // =>  HOOKS
    onBeforeUnmount(stopPolling);

    // =>  DATA
    return {
      headers,
      icons: { mdiRssBox },
      isOpen,
      items,
      itemStatus,
      loading,
      mediaTypeLabel,
      reasonLabel,
      request,
      requestStatus,
      subtitle,
      title,
    };
  },
};
</script>

<style lang="scss" scoped>
.srq-detail__summary {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  font-size: $rmr-fs-body;
  color: $rmr-text-secondary;
}

.srq-detail__ok {
  color: $rmr-success;
}

.srq-detail__ko {
  color: $rmr-orange;
}

.srq-detail__err {
  color: $rmr-danger;
}

.srq-detail__pending {
  flex-basis: 100%;
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
}

.srq-detail__url {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.srq-detail__reason {
  font-weight: 700;
  color: $rmr-text-primary;
}

.srq-detail__media {
  font-weight: 400;
  color: $rmr-text-muted;
}

.srq-detail__reason-detail {
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;

  &:hover {
    text-overflow: clip;

    .srq-detail__marquee {
      display: inline-block;
      animation: srq-marquee 14s ease-in-out infinite;
      will-change: transform;
      backface-visibility: hidden;
      transform: translateZ(0);
      -webkit-font-smoothing: subpixel-antialiased;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .srq-detail__reason-detail:hover .srq-detail__marquee {
    animation: none;
  }
}

@keyframes srq-marquee {
  0%,
  20% {
    transform: translate3d(0, 0, 0);
  }
  65%,
  80% {
    transform: translate3d(min(0px, calc(-100% + 340px)), 0, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
}
</style>
